import { FC, useState } from 'react';
import { ShowError } from '../../../errors/components/ShowError';
import {
	useBindCategoryGroup,
	useUnbindCategoryGroup,
} from '../api/telegram.mutations';
import {
	useGetCategories,
	useGetCategoryGroups,
	useGetManagerStatus,
} from '../api/telegram.queries';
import { ManagerBotStatus } from '../model';

const formatDateTime = (iso?: string | null): string => {
	if (!iso) return '—';
	return new Date(iso).toLocaleString('ru-RU', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
};

/**
 * Привязка супергрупп к категориям.
 *
 * Код выдаёт бот платформы командой /bind в самой группе — здесь
 * оператор только сообщает, за какой категорией её закрепить.
 */
/**
 * Здоровье бота-манагера — того, через которого продавцы создают ботов своих
 * магазинов.
 *
 * Место именно здесь, а не в админке продавца: бот принадлежит платформе,
 * продавец им не владеет и включить режим в @BotFather не может. Ему такой
 * алерт был бы тупиком, а нам — утечкой служебного имени.
 *
 * Симптомов у поломки нет никаких: Telegram просто перестаёт отдавать и
 * ссылку на создание, и токены — сразу у всех продавцов.
 */
const ManagerBotAlert: FC<{ status?: ManagerBotStatus }> = ({ status }) => {
	// Манагер не настроен вовсе — это не поломка, а работа по-старому:
	// продавцы переносят токен от @BotFather руками.
	if (!status || (!status.enabled && !status.username)) return null;
	if (status.enabled && status.can_manage_bots) return null;

	const notResponding = !status.enabled;

	return (
		<div className='alert alert-warning d-flex flex-column mb-5'>
			<h4 className='mb-1'>Продавцы не могут создавать ботов</h4>
			{notResponding ? (
				<span>
					Бот-манагер{' '}
					{status.username ? <code>@{status.username}</code> : null} не
					отвечает: не задан токен либо недоступен сервис ботов. Проверьте
					<code className='mx-1'>TELEGRAM_MANAGER_BOT_TOKEN</code>
					и состояние mini-app-bot.
				</span>
			) : (
				<span>
					У бота <code>@{status.username}</code> выключен режим управления
					ботами. Откройте @BotFather → этот бот → Bot Management Mode →
					Enable. Пока он выключен, Telegram не отдаёт ни ссылку на
					создание, ни токены — у всех продавцов сразу.
				</span>
			)}
		</div>
	);
};

export const CategoryGroupsPage: FC = () => {
	const { data, isLoading, error } = useGetCategoryGroups();
	const { data: categoriesData } = useGetCategories();
	const { data: manager } = useGetManagerStatus();

	const bind = useBindCategoryGroup();
	const unbind = useUnbindCategoryGroup();

	const [code, setCode] = useState('');
	const [categoryId, setCategoryId] = useState('');
	const [formError, setFormError] = useState<string | null>(null);

	const groups = data?.groups ?? [];
	const categories = categoriesData?.categories ?? [];
	const boundIds = new Set(groups.map(group => group.category_id));
	const freeCategories = categories.filter(c => !boundIds.has(c.id));

	const handleBind = async () => {
		setFormError(null);

		if (!code.trim() || !categoryId) {
			setFormError('Укажите код и категорию');
			return;
		}

		try {
			await bind.mutateAsync({
				code: code.trim().toUpperCase(),
				category_id: Number(categoryId),
			});
			setCode('');
			setCategoryId('');
		} catch (e: any) {
			setFormError(e?.response?.data?.detail ?? 'Не удалось привязать группу');
		}
	};

	const handleUnbind = async (id: number, name: string) => {
		if (
			!window.confirm(
				`Открепить группу от категории «${name}»?\n\n` +
					'Топики магазинов и опубликованные объявления в Telegram ' +
					'останутся, но новые публикации прекратятся.'
			)
		) {
			return;
		}
		await unbind.mutateAsync(id);
	};

	if (error) return <ShowError error={error} />;

	return (
		<>
			<ManagerBotAlert status={manager} />

			<div className='card mb-5'>
				<div className='card-header'>
					<h3 className='card-title'>Привязать группу к категории</h3>
				</div>
				<div className='card-body'>
					<div className='alert alert-light-primary'>
						В супергруппе с включёнными темами выполните{' '}
						<code>/bind</code> ботом платформы — он выдаст код на 15 минут.
						Бот должен быть администратором с правом управления темами.
					</div>

					<div className='row g-4 align-items-end'>
						<div className='col-md-4'>
							<label className='form-label'>Код привязки</label>
							<input
								type='text'
								className='form-control'
								placeholder='ABC123'
								value={code}
								maxLength={16}
								onChange={e => setCode(e.target.value)}
							/>
						</div>
						<div className='col-md-5'>
							<label className='form-label'>Категория</label>
							<select
								className='form-select'
								value={categoryId}
								onChange={e => setCategoryId(e.target.value)}
							>
								<option value=''>Выберите категорию</option>
								{freeCategories.map(category => (
									<option key={category.id} value={category.id}>
										{category.name}
									</option>
								))}
							</select>
							{freeCategories.length === 0 && (
								<div className='form-text'>
									У всех категорий уже есть группы
								</div>
							)}
						</div>
						<div className='col-md-3'>
							<button
								type='button'
								className='btn btn-primary w-100'
								disabled={bind.isLoading}
								onClick={handleBind}
							>
								{bind.isLoading ? 'Привязываем...' : 'Привязать'}
							</button>
						</div>
					</div>

					{formError && (
						<div className='alert alert-danger mt-4 mb-0'>{formError}</div>
					)}
				</div>
			</div>

			<div className='card'>
				<div className='card-header'>
					<h3 className='card-title'>Группы категорий</h3>
				</div>
				<div className='card-body'>
					{isLoading ? (
						<div className='text-muted'>Загрузка...</div>
					) : groups.length === 0 ? (
						<div className='text-muted'>
							Пока ни одна группа не привязана. Магазины без группы не смогут
							публиковать объявления.
						</div>
					) : (
						<div className='table-responsive'>
							<table className='table table-row-bordered align-middle'>
								<thead>
									<tr className='fw-bold text-muted'>
										<th>Категория</th>
										<th>Группа</th>
										<th>Chat ID</th>
										<th>Обновлено</th>
										<th className='text-end'>Действия</th>
									</tr>
								</thead>
								<tbody>
									{groups.map(group => (
										<tr key={group.id}>
											<td className='fw-bold'>{group.category_name}</td>
											<td>{group.chat_title ?? '—'}</td>
											<td>
												<code>{group.chat_id}</code>
											</td>
											<td className='text-muted'>
												{formatDateTime(group.updated)}
											</td>
											<td className='text-end'>
												<button
													type='button'
													className='btn btn-sm btn-light-danger'
													disabled={unbind.isLoading}
													onClick={() =>
														handleUnbind(
															group.category_id,
															group.category_name
														)
													}
												>
													Открепить
												</button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>
			</div>
		</>
	);
};
